import type { DemolitionWeapon } from '../../services/damageModel';
import type { DemolitionEffectTier } from '../presentation/effectTier';

/** Same mixer as Phase1DamageLayer.hash01 — event-driven spawn must stay deterministic. */
export function hash01(seed: number): number {
  let h = Math.imul(seed | 0, 0x9e3779b1) ^ 0x6d2b79f5;
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  h = Math.imul(h ^ (h >>> 15), 0x846ca68b);
  return ((h ^ (h >>> 16)) >>> 0) / 0x1_0000_0000;
}

/** Quantized event + position seed so identical scenarios replay identically. */
export function eventParticleSeed(
  sequence: number,
  x: number,
  y: number,
  z: number,
  index: number,
  salt = 0,
): number {
  const qx = Math.round(x * 100);
  const qy = Math.round(y * 100);
  const qz = Math.round(z * 100);
  return (
    Math.imul(sequence | 0, 73856093) ^
    Math.imul(qx, 19349663) ^
    Math.imul(qy, 83492791) ^
    Math.imul(qz, 50331653) ^
    Math.imul(index + 1, 2654435761) ^
    (salt | 0)
  );
}

/** Long-tail unit in [0,1]: many small, few large. */
export function longTailUnit(u: number): number {
  const t = Math.min(1, Math.max(0, u));
  return t * t * t;
}

export function longTailScale(u: number, min: number, max: number): number {
  return min + (max - min) * longTailUnit(u);
}

/** Burst dust card radius (world units). Long-tail: many ~0.22, few ~0.62. */
export const DUST_SCALE_MIN = 0.22;
export const DUST_SCALE_MAX = 0.62;
/** Stone heavy-fines: smaller cards, denser core. */
export const DUST_HEAVY_SCALE_MIN = 0.10;
export const DUST_HEAVY_SCALE_MAX = 0.28;
export const DUST_LANDING_SCALE_MIN = 0.04;
export const DUST_LANDING_SCALE_MAX = 0.09;
/** Low rubble sheet: moderate cards in a wide ellipse, not a few dinner plates. */
export const DUST_APRON_SCALE_MIN = 0.22;
export const DUST_APRON_SCALE_MAX = 0.45;
export const DUST_PLUME_SCALE_MIN = 0.4;
export const DUST_PLUME_SCALE_MAX = 0.9;
export const DUST_TRACE_SCALE_MIN = 0.06;
export const DUST_TRACE_SCALE_MAX = 0.16;
/** High tier may use a slightly larger max card. */
export const DUST_HIGH_TIER_MAX_MUL = 1.2;
/** Push cards off the wall so soft-depth fade does not wipe a parallel disc. */
export const DUST_WALL_OFFSET = 0.24;
export const DUST_GROWTH_START = 0.65;
export const DUST_GROWTH_END = 2.2;
/** Pull pale descriptor colors down so cards separate from a lit concrete wall. */
export const DUST_TARGET_LUMINANCE = 0.62;
/** Two-tone brick: mortar is dusty grey-brown; clay is terracotta powder, not sparkle. */
export const DUST_MORTAR_LUMINANCE = 0.48;
export const DUST_CLAY_LUMINANCE = 0.3;

export type DustScaleKind = 'burst' | 'heavy' | 'landing' | 'plume' | 'trace' | 'apron';

export function dustCardScale(u: number, kind: DustScaleKind, highTier = false): number {
  const high = highTier ? DUST_HIGH_TIER_MAX_MUL : 1;
  switch (kind) {
    case 'heavy':
      return longTailScale(u, DUST_HEAVY_SCALE_MIN, DUST_HEAVY_SCALE_MAX * high);
    case 'landing':
      return longTailScale(u, DUST_LANDING_SCALE_MIN, DUST_LANDING_SCALE_MAX * high);
    case 'plume':
      return longTailScale(u, DUST_PLUME_SCALE_MIN, DUST_PLUME_SCALE_MAX * high);
    case 'trace':
      return longTailScale(u, DUST_TRACE_SCALE_MIN, DUST_TRACE_SCALE_MAX * high);
    case 'apron':
      return longTailScale(u, DUST_APRON_SCALE_MIN, DUST_APRON_SCALE_MAX * high);
    default:
      return longTailScale(u, DUST_SCALE_MIN, DUST_SCALE_MAX * high);
  }
}

/** Grow slowly through the entire life — mass-conserving fade uses grown area, not a collapse. */
export function dustLifeScale(age: number, life: number): number {
  const t = Math.min(1, Math.max(0, age / Math.max(life, 1e-6)));
  return DUST_GROWTH_START + t * (DUST_GROWTH_END - DUST_GROWTH_START);
}

/** Rec. 709 luminance. */
export function rgbLuminance(rgb: { r: number; g: number; b: number }): number {
  return 0.2126 * rgb.r + 0.7152 * rgb.g + 0.0722 * rgb.b;
}

/** Scale a color down so it does not exceed `target` luminance. Hue preserved. */
export function towardLuminance(
  rgb: { r: number; g: number; b: number },
  target = DUST_TARGET_LUMINANCE,
): { r: number; g: number; b: number } {
  const lum = rgbLuminance(rgb);
  if (lum <= target || lum < 1e-6) return rgb;
  const s = target / lum;
  return { r: rgb.r * s, g: rgb.g * s, b: rgb.b * s };
}

/**
 * Scale a color to an exact luminance target (up or down). Used for two-tone
 * brick so mortar and clay do not share the shared pale ceiling.
 */
export function fitLuminance(
  rgb: { r: number; g: number; b: number },
  target: number,
): { r: number; g: number; b: number } {
  const lum = rgbLuminance(rgb);
  if (lum < 1e-6) return rgb;
  const s = target / lum;
  return {
    r: Math.min(1, Math.max(0, rgb.r * s)),
    g: Math.min(1, Math.max(0, rgb.g * s)),
    b: Math.min(1, Math.max(0, rgb.b * s)),
  };
}

export interface WeaponBurstShape {
  /** Half-angle from the burst axis, radians. */
  readonly spreadRad: number;
  /** 1 = fully along the axis; 0 = in the surface plane. */
  readonly along: number;
  readonly speedMul: number;
  readonly lifeMul: number;
  readonly dustCount: number;
  readonly debrisCount: number;
  readonly streakCount: number;
  /** Burst-line radial vs axial scale. */
  readonly burstRadial: number;
  readonly burstAxial: number;
}

export function weaponBurstShape(weapon: DemolitionWeapon): WeaponBurstShape {
  switch (weapon) {
    case 'spike':
      return {
        spreadRad: (15 * Math.PI) / 180,
        along: 0.95,
        speedMul: 2.4,
        lifeMul: 0.85,
        dustCount: 20,
        debrisCount: 6,
        streakCount: 3,
        burstRadial: 0.38,
        burstAxial: 1.55,
      };
    case 'rubble':
      return {
        spreadRad: (80 * Math.PI) / 180,
        along: 0.14,
        speedMul: 0.48,
        lifeMul: 0.62,
        dustCount: 34,
        debrisCount: 10,
        streakCount: 0,
        burstRadial: 1.55,
        burstAxial: 0.28,
      };
    case 'concussion':
      return {
        spreadRad: (50 * Math.PI) / 180,
        along: 0.4,
        speedMul: 0.55,
        lifeMul: 0.7,
        dustCount: 10,
        debrisCount: 2,
        streakCount: 0,
        burstRadial: 0.72,
        burstAxial: 0.72,
      };
    case 'bomb':
      return {
        spreadRad: (55 * Math.PI) / 180,
        along: 0.35,
        speedMul: 0.8,
        lifeMul: 1.15,
        dustCount: 16,
        debrisCount: 4,
        streakCount: 0,
        burstRadial: 1,
        burstAxial: 1,
      };
    case 'cannon':
    default:
      return {
        spreadRad: (38 * Math.PI) / 180,
        along: 0.62,
        speedMul: 1,
        lifeMul: 1,
        dustCount: 26,
        debrisCount: 7,
        streakCount: 0,
        burstRadial: 1,
        burstAxial: 1,
      };
  }
}

export function orthonormalBasis(normal: { x: number; y: number; z: number }): {
  nx: number;
  ny: number;
  nz: number;
  ux: number;
  uy: number;
  uz: number;
  vx: number;
  vy: number;
  vz: number;
} {
  const n = Math.hypot(normal.x, normal.y, normal.z) || 1;
  const nx = normal.x / n;
  const ny = normal.y / n;
  const nz = normal.z / n;
  const rx = Math.abs(ny) < 0.9 ? 0 : 1;
  const ry = Math.abs(ny) < 0.9 ? 1 : 0;
  const rz = 0;
  const tx = ry * nz - rz * ny;
  const ty = rz * nx - rx * nz;
  const tz = rx * ny - ry * nx;
  const tangentLength = Math.hypot(tx, ty, tz) || 1;
  const ux = tx / tangentLength;
  const uy = ty / tangentLength;
  const uz = tz / tangentLength;
  return {
    nx,
    ny,
    nz,
    ux,
    uy,
    uz,
    vx: ny * uz - nz * uy,
    vy: nz * ux - nx * uz,
    vz: nx * uy - ny * ux,
  };
}

/** Cone sample around `normal`. `spread01` in [0,1] maps to [0, spreadRad]. */
export function coneDirection(
  basis: ReturnType<typeof orthonormalBasis>,
  azimuth: number,
  spread01: number,
  spreadRad: number,
  along: number,
): { x: number; y: number; z: number } {
  const cone = spread01 * spreadRad;
  const radial = Math.sin(cone) * (1 - along * 0.35);
  const axial = Math.cos(cone) * along + (1 - along) * 0.12;
  const cx = Math.cos(azimuth);
  const sy = Math.sin(azimuth);
  const x = basis.nx * axial + (basis.ux * cx + basis.vx * sy) * radial;
  const y = basis.ny * axial + (basis.uy * cx + basis.vy * sy) * radial;
  const z = basis.nz * axial + (basis.uz * cx + basis.vz * sy) * radial;
  const len = Math.hypot(x, y, z) || 1;
  return { x: x / len, y: y / len, z: z / len };
}

export function scaleCount(base: number, multiplier: number): number {
  if (multiplier <= 0 || base <= 0) return 0;
  return Math.max(1, Math.round(base * multiplier));
}

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const raw = hex.startsWith('#') ? hex.slice(1) : hex;
  const n = Number.parseInt(raw.length === 3
    ? raw.split('').map((c) => c + c).join('')
    : raw, 16);
  return {
    r: ((n >> 16) & 255) / 255,
    g: ((n >> 8) & 255) / 255,
    b: (n & 255) / 255,
  };
}

export function scaleFromVolume(volume: number): number {
  const side = Math.cbrt(Math.max(volume, 1e-6));
  return Math.min(0.14, Math.max(0.018, side * 0.85));
}

export const DEBRIS_LIFE = 4.2;
/** Long enough that a thin floor film is still on camera at 6 s. */
export const DUST_LIFE = 7.4;
export const STREAK_LIFE = 0.28;
export const PLUME_LIFE = 8.2;
export const SHOCKWAVE_LIFE = 0.32;
export const BURST_LIFE = 0.24;
export const SOFT_FADE_DISTANCE = 0.1;
/** Camera-proximity fade: alpha 0 inside start, full by end (world units). */
export const DUST_NEAR_FADE_START = 0.45;
export const DUST_NEAR_FADE_END = 1.0;
/** Ground / landing puffs: shorter than burst, still long enough to thin across several tiles. */
export const DUST_LANDING_LIFE = 3.8;
export const DUST_APRON_LIFE = 4.4;
/** Burst puff initial speed (m/s) before weapon/material multipliers. */
export const DUST_BURST_SPEED_MIN = 1.05;
export const DUST_BURST_SPEED_MAX = 2.4;
/** Exponential drag time-constant: ~0.6s to near-stationary (e^{-0.6/0.18} ≈ 0.036). */
export const DUST_DRAG_TAU = 0.18;
export const DUST_GROUND_Y = 0.045;
export const DUST_GROUND_DRAG_TAU = 0.08;
/** Hold near-full alpha, then fade ∝ 1/grownArea toward a thin linger film. */
export const DUST_ALPHA_HOLD = 0.25;
/** Visible film through most of life; last 8% fades to 0. */
export const DUST_ALPHA_FLOOR = 0.12;
export const DUST_ALPHA_TAIL = 0.08;
/** Concrete / brick / wood / stone walls are 4.2 m tall (glass 3.2). */
export const DEFAULT_WALL_HEIGHT = 4.2;
/** Only the bomb plume may climb, and it must stop below this multiple of wall height. */
export const PLUME_MAX_HEIGHT_MUL = 1.6;
/** Bomb plume: buoyant rise, then the column stops and mushrooms. Never an infinite updraft. */
export const PLUME_BUOYANT_AGE = 1.2;
export const PLUME_BUOYANT_ACCEL = 3.0;
/** CPU bomb plume birth speed (m/s) — visible chimney for ~1.2 s, then the 1.6× cap. */
export const PLUME_RISE_SPEED_MIN = 2.5;
export const PLUME_RISE_SPEED_MAX = 3.5;
export const PLUME_MUSHROOM_ACCEL = 0.55;
export const PLUME_POST_SETTLE = 0.12;
export const PLUME_RISE_DRAG_TAU = 0.85;
export const PLUME_RISE_KILL_TAU = 0.28;
export const PLUME_DRIFT_TAU = 1.6;
export const DUST_MORTAR_SCALE_MUL = 2.05;
export const DUST_CLAY_SCALE_MUL = 0.58;
export const DUST_MORTAR_FRACTION = 0.52;
/** Mortar stays in the wall plane; clay sprays along the outward normal. */
export const DUST_MORTAR_ALONG = 0.08;
export const DUST_CLAY_ALONG = 0.88;
export const DUST_MORTAR_SPREAD_RAD = (85 * Math.PI) / 180;
export const DUST_CLAY_SPREAD_RAD = (28 * Math.PI) / 180;
export const DUST_MORTAR_SPEED_MUL = 0.42;
export const DUST_CLAY_SPEED_MUL = 1.45;
/** Cap billboard radius vs camera distance so near cards cannot fill the frame. */
export const DUST_SCREEN_RADIUS = 0.13;
/** Ground-hugging cards (y < this) may occupy more of the frame — they are a sheet, not orbs. */
export const DUST_GROUND_HUG_Y = 0.6;
export const DUST_GROUND_SCREEN_RADIUS = 0.25;
/** Apron sheet at the wall foot: previous 0.25 ground cap hid the silhouette. */
export const DUST_APRON_SCREEN_RADIUS = 0.42;
/** Rubble apron ellipse (metres): wide along the wall, shallow into the room. */
export const DUST_APRON_WALL_SPAN = 0.95;
export const DUST_APRON_ROOM_SPAN = 0.9;
export const DUST_APRON_HEIGHT_MAX = 0.5;
export const DUST_APRON_OUT_MIN = 0.16;
/** Isolated large cards become a lumpy cluster instead of one circle. */
export const DUST_CLUSTER_SCALE_THRESHOLD = 0.3;
export const DUST_CLUSTER_SCALE_MIN = 0.6;
export const DUST_CLUSTER_SCALE_MAX = 0.75;
/** Larger cards are a faint haze — `alpha *= clamp(0.28 / scale, 0.40, 1)`. */
export const DUST_SCALE_ALPHA_REF = 0.28;
export const DUST_SCALE_ALPHA_MIN = 0.4;
/** Non-apron cards near the floor lose opacity/size so they cannot headlight. */
export const DUST_FLOOR_FADE_Y = 0.55;
export const DUST_FLOOR_ALPHA_MIN = 0.32;
export const DUST_FLOOR_SCALE_MIN = 0.42;
export const DUST_LANDING_ALPHA = 0.14;
export const DUST_LANDING_COUNT = 1;
/** Cannon dust cone half-angle. Weapon debris/streak spread is unchanged. */
export const CANNON_DUST_SPREAD_RAD = (50 * Math.PI) / 180;
export const FX_LAYER = 1;

/** GLSL smoothstep in JS — camera-near fade of a dust fragment. */
export function dustNearFade(viewDistance: number): number {
  const span = DUST_NEAR_FADE_END - DUST_NEAR_FADE_START;
  const t = Math.min(1, Math.max(0, (viewDistance - DUST_NEAR_FADE_START) / span));
  return t * t * (3 - 2 * t);
}

/** Landing cards keep growing (same envelope as burst) — fade is via alpha, not a scale cliff. */
export function dustLandingLifeScale(age: number, life = DUST_LANDING_LIFE): number {
  return dustLifeScale(age, life);
}

/**
 * Dispersion envelope: near-full alpha for the first ~25% of life, then
 * α ∝ 1 / grownScale² (mass conservation look) down to `DUST_ALPHA_FLOOR`.
 * The last `DUST_ALPHA_TAIL` of life fades the film to 0.
 */
export function dustAlphaEnvelope(age: number, life: number): number {
  const duration = Math.max(life, 1e-6);
  const t = Math.min(1, Math.max(0, age / duration));
  if (t <= DUST_ALPHA_HOLD) return 1;
  const sHold = dustLifeScale(duration * DUST_ALPHA_HOLD, duration);
  const sNow = dustLifeScale(age, duration);
  const sEnd = dustLifeScale(duration, duration);
  const invNow = (sHold * sHold) / Math.max(sNow * sNow, 1e-8);
  const invEnd = (sHold * sHold) / Math.max(sEnd * sEnd, 1e-8);
  const denom = 1 - invEnd;
  const faded = denom < 1e-6 ? Math.max(0, 1 - t) : Math.max(0, (invNow - invEnd) / denom);
  const film = Math.max(DUST_ALPHA_FLOOR, faded);
  const tailStart = 1 - DUST_ALPHA_TAIL;
  if (t <= tailStart) return film;
  const u = (t - tailStart) / DUST_ALPHA_TAIL;
  return film * (1 - u);
}

export function dustDragMul(dt: number, tau = DUST_DRAG_TAU): number {
  return Math.exp(-dt / Math.max(tau, 1e-4));
}

/** Hot-gas buoyancy. Full at ignition, zero at/after `PLUME_BUOYANT_AGE`. Cold dust must not call this. */
export function plumeBuoyancyAccel(age: number): number {
  if (age >= PLUME_BUOYANT_AGE) return 0;
  if (age <= 0) return PLUME_BUOYANT_ACCEL;
  const t = age / PLUME_BUOYANT_AGE;
  return PLUME_BUOYANT_ACCEL * (1 - t) * (1 - t);
}

/** Initial upward speed of a CPU bomb-plume card. GPU plume cards must not use this. */
export function plumeBirthSpeed(u: number): number {
  const t = Math.min(1, Math.max(0, u));
  return PLUME_RISE_SPEED_MIN + (PLUME_RISE_SPEED_MAX - PLUME_RISE_SPEED_MIN) * t;
}

/**
 * Brick's descriptor linger (0.92) dies before t12 on the close-cam strip.
 * Lift it to at least concrete-ish so a faint film lasts the full 6 s.
 */
export function effectiveDustLingerScale(family: string, base: number): number {
  if (family === 'brick') return Math.max(base, 1.2);
  return base;
}

export interface DustMotionState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  age: number;
  applyUpdraft: boolean;
  settleSpeed: number;
  groundHugging: boolean;
  /** World-Y ceiling. Plume uses 1.6× wall height; other dust uses wall height. */
  maxY?: number;
}

/**
 * Billow → hang → settle for masonry dust. `applyUpdraft` is bomb-plume only:
 * buoyant rise for ~1.2s, then the column stops, mushrooms laterally, and drifts.
 */
export function stepDustMotion(p: DustMotionState, dt: number): void {
  if (p.applyUpdraft) {
    if (p.age < PLUME_BUOYANT_AGE) {
      p.vy += plumeBuoyancyAccel(p.age) * dt;
      const damp = dustDragMul(dt, PLUME_RISE_DRAG_TAU);
      p.vx *= damp;
      p.vz *= damp;
    } else {
      if (p.vy > 0) p.vy *= dustDragMul(dt, PLUME_RISE_KILL_TAU);
      const radial = Math.hypot(p.vx, p.vz);
      if (radial > 1e-5) {
        p.vx += (p.vx / radial) * PLUME_MUSHROOM_ACCEL * dt;
        p.vz += (p.vz / radial) * PLUME_MUSHROOM_ACCEL * dt;
      }
      const drift = dustDragMul(dt, PLUME_DRIFT_TAU);
      p.vx *= drift;
      p.vz *= drift;
      const settle = dustDragMul(dt, 0.5);
      p.vy = -PLUME_POST_SETTLE + (p.vy + PLUME_POST_SETTLE) * settle;
    }
  } else {
    const damp = dustDragMul(dt);
    p.vx *= damp;
    p.vz *= damp;
    p.vy = -p.settleSpeed + (p.vy + p.settleSpeed) * damp;
  }

  p.x += p.vx * dt;
  p.y += p.vy * dt;
  p.z += p.vz * dt;

  const ceiling = p.maxY ?? (p.applyUpdraft
    ? DEFAULT_WALL_HEIGHT * PLUME_MAX_HEIGHT_MUL
    : DEFAULT_WALL_HEIGHT);
  if (p.y > ceiling) {
    p.y = ceiling;
    if (p.vy > 0) {
      const radial = Math.hypot(p.vx, p.vz);
      if (radial > 1e-5) {
        p.vx += (p.vx / radial) * p.vy * 0.45;
        p.vz += (p.vz / radial) * p.vy * 0.45;
      }
      p.vy = 0;
    }
  }

  if (p.y <= DUST_GROUND_Y) {
    p.y = DUST_GROUND_Y;
    p.groundHugging = true;
    p.vy = 0;
    const ground = dustDragMul(dt, DUST_GROUND_DRAG_TAU);
    p.vx *= ground;
    p.vz *= ground;
  }
}

export function burstDustSpeed(u: number, speedMul: number, weaponSpeedMul: number): number {
  const base = DUST_BURST_SPEED_MIN + Math.min(1, Math.max(0, u)) * (DUST_BURST_SPEED_MAX - DUST_BURST_SPEED_MIN);
  const weapon = 0.62 + 0.38 * weaponSpeedMul;
  return base * weapon * speedMul;
}

export type DebrisFamily = 'concrete' | 'brick' | 'wood' | 'stone';

export const DEBRIS_FAMILIES: readonly DebrisFamily[] = ['concrete', 'brick', 'wood', 'stone'];
export const DEBRIS_SHARD_VARIANTS = 3;

export function debrisFamilyOf(id: string): DebrisFamily {
  return (DEBRIS_FAMILIES as readonly string[]).includes(id) ? (id as DebrisFamily) : 'concrete';
}

export const DEBRIS_RESTITUTION: Record<DebrisFamily, number> = {
  stone: 0.04,
  concrete: 0.08,
  brick: 0.12,
  wood: 0.28,
};

/** Horizontal velocity multiplier on each ground contact. */
export const DEBRIS_GROUND_FRICTION: Record<DebrisFamily, number> = {
  stone: 0.38,
  concrete: 0.42,
  brick: 0.48,
  wood: 0.55,
};

export const DEBRIS_GROUND_Y = 0.018;
/** Ground / support contact band. Freeze is illegal above this unless still for DEBRIS_STILL_DURATION. */
export const DEBRIS_CONTACT_Y = 0.15;
export const DEBRIS_SETTLE_SPEED = 0.08;
export const DEBRIS_STILL_SPEED = 0.05;
export const DEBRIS_STILL_DURATION = 0.8;
export const DEBRIS_AIR_DRAG = 0.35;
export const DEBRIS_SPIN_DAMP = 1.6;
export const DEBRIS_CONTACT_SPIN = 0.32;
export const DEBRIS_FADE_START = 0.82;

export interface QuatState {
  qx: number;
  qy: number;
  qz: number;
  qw: number;
}

export interface DebrisMotionState extends QuatState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  wx: number;
  wy: number;
  wz: number;
  gravity: number;
  restitution: number;
  friction: number;
  alignVelocity: boolean;
  settled: boolean;
  onGround: boolean;
  /** Continuous time spent below DEBRIS_STILL_SPEED. */
  stillTime: number;
}

export function quatNormalize(q: QuatState): void {
  const len = Math.hypot(q.qx, q.qy, q.qz, q.qw) || 1;
  q.qx /= len;
  q.qy /= len;
  q.qz /= len;
  q.qw /= len;
}

export function quatFromAxisAngle(ax: number, ay: number, az: number, angle: number): QuatState {
  const len = Math.hypot(ax, ay, az) || 1;
  const half = angle * 0.5;
  const s = Math.sin(half) / len;
  return { qx: ax * s, qy: ay * s, qz: az * s, qw: Math.cos(half) };
}

export function quatMul(a: QuatState, b: QuatState): QuatState {
  return {
    qx: a.qw * b.qx + a.qx * b.qw + a.qy * b.qz - a.qz * b.qy,
    qy: a.qw * b.qy - a.qx * b.qz + a.qy * b.qw + a.qz * b.qx,
    qz: a.qw * b.qz + a.qx * b.qy - a.qy * b.qx + a.qz * b.qw,
    qw: a.qw * b.qw - a.qx * b.qx - a.qy * b.qy - a.qz * b.qz,
  };
}

export function quatSlerp(a: QuatState, b: QuatState, t: number): QuatState {
  let ax = a.qx;
  let ay = a.qy;
  let az = a.qz;
  let aw = a.qw;
  let dot = ax * b.qx + ay * b.qy + az * b.qz + aw * b.qw;
  if (dot < 0) {
    ax = -ax;
    ay = -ay;
    az = -az;
    aw = -aw;
    dot = -dot;
  }
  if (dot > 0.9995) {
    const out = {
      qx: ax + (b.qx - ax) * t,
      qy: ay + (b.qy - ay) * t,
      qz: az + (b.qz - az) * t,
      qw: aw + (b.qw - aw) * t,
    };
    quatNormalize(out);
    return out;
  }
  const theta = Math.acos(Math.min(1, dot));
  const s = Math.sin(theta);
  const w0 = Math.sin((1 - t) * theta) / s;
  const w1 = Math.sin(t * theta) / s;
  return {
    qx: ax * w0 + b.qx * w1,
    qy: ay * w0 + b.qy * w1,
    qz: az * w0 + b.qz * w1,
    qw: aw * w0 + b.qw * w1,
  };
}

/** Rotate +Z onto unit vector `(tx,ty,tz)`. */
export function quatFromPlusZ(tx: number, ty: number, tz: number): QuatState {
  const len = Math.hypot(tx, ty, tz) || 1;
  const x = tx / len;
  const y = ty / len;
  const z = tz / len;
  if (z > 0.9999) return { qx: 0, qy: 0, qz: 0, qw: 1 };
  if (z < -0.9999) return { qx: 0, qy: 1, qz: 0, qw: 0 };
  const axisLen = Math.hypot(-y, x, 0) || 1;
  const angle = Math.acos(Math.min(1, Math.max(-1, z)));
  return quatFromAxisAngle(-y / axisLen, x / axisLen, 0, angle);
}

export function rotateByQuat(q: QuatState, x: number, y: number, z: number): { x: number; y: number; z: number } {
  const ix = q.qw * x + q.qy * z - q.qz * y;
  const iy = q.qw * y + q.qz * x - q.qx * z;
  const iz = q.qw * z + q.qx * y - q.qy * x;
  const iw = -q.qx * x - q.qy * y - q.qz * z;
  return {
    x: ix * q.qw + iw * -q.qx + iy * -q.qz - iz * -q.qy,
    y: iy * q.qw + iw * -q.qy + iz * -q.qx - ix * -q.qz,
    z: iz * q.qw + iw * -q.qz + ix * -q.qy - iy * -q.qx,
  };
}

function applyQuat(p: QuatState, q: QuatState): void {
  p.qx = q.qx;
  p.qy = q.qy;
  p.qz = q.qz;
  p.qw = q.qw;
}

/** Rotation whose columns are the images of +X, +Y, +Z. */
function quatFromBasis(
  xx: number, xy: number, xz: number,
  yx: number, yy: number, yz: number,
  zx: number, zy: number, zz: number,
): QuatState {
  const trace = xx + yy + zz;
  if (trace > 0) {
    const s = 2 * Math.sqrt(trace + 1);
    return { qw: 0.25 * s, qx: (zy - yz) / s, qy: (xz - zx) / s, qz: (yx - xy) / s };
  }
  if (xx > yy && xx > zz) {
    const s = 2 * Math.sqrt(1 + xx - yy - zz);
    return { qw: (zy - yz) / s, qx: 0.25 * s, qy: (xy + yx) / s, qz: (xz + zx) / s };
  }
  if (yy > zz) {
    const s = 2 * Math.sqrt(1 + yy - xx - zz);
    return { qw: (xz - zx) / s, qx: (xy + yx) / s, qy: 0.25 * s, qz: (yz + zy) / s };
  }
  const s = 2 * Math.sqrt(1 + zz - xx - yy);
  return { qw: (yx - xy) / s, qx: (xz + zx) / s, qy: (yz + zy) / s, qz: 0.25 * s };
}

export function integrateSpin(p: DebrisMotionState, dt: number): void {
  const speed = Math.hypot(p.wx, p.wy, p.wz);
  if (speed < 1e-6) return;
  const delta = quatFromAxisAngle(p.wx / speed, p.wy / speed, p.wz / speed, speed * dt);
  applyQuat(p, quatMul(p, delta));
  quatNormalize(p);
}

export function slerpTowardPlusZ(
  p: QuatState,
  tx: number,
  ty: number,
  tz: number,
  t: number,
): void {
  applyQuat(p, quatSlerp(p, quatFromPlusZ(tx, ty, tz), Math.min(1, Math.max(0, t))));
}

/**
 * Lie a splinter flat: long (+Z) axis in the ground plane AND thin (+Y) axis
 * world-up, so the plank rests on its face instead of a stacked-on-edge jumble.
 */
export function flattenOntoGround(p: QuatState, t: number): void {
  const axis = rotateByQuat(p, 0, 0, 1);
  axis.y = 0;
  const len = Math.hypot(axis.x, axis.z);
  const lx = len < 1e-5 ? 1 : axis.x / len;
  const lz = len < 1e-5 ? 0 : axis.z / len;
  const target = quatFromBasis(
    lz, 0, -lx,
    0, 1, 0,
    lx, 0, lz,
  );
  applyQuat(p, quatSlerp(p, target, Math.min(1, Math.max(0, t))));
}

export function stepDebrisMotion(p: DebrisMotionState, dt: number): void {
  if (p.settled) {
    p.y = Math.max(p.y, DEBRIS_GROUND_Y);
    if (p.alignVelocity) flattenOntoGround(p, 1);
    return;
  }

  const air = Math.exp(-DEBRIS_AIR_DRAG * dt);
  p.vx *= air;
  p.vz *= air;
  p.vy = (p.vy - p.gravity * dt) * air;

  p.x += p.vx * dt;
  p.y += p.vy * dt;
  p.z += p.vz * dt;

  const spinDamp = Math.exp(-DEBRIS_SPIN_DAMP * dt);
  p.wx *= spinDamp;
  p.wy *= spinDamp;
  p.wz *= spinDamp;
  integrateSpin(p, dt);

  if (p.alignVelocity && !p.onGround) {
    const speed = Math.hypot(p.vx, p.vy, p.vz);
    if (speed > 0.15) {
      slerpTowardPlusZ(p, p.vx / speed, p.vy / speed, p.vz / speed, 1 - Math.exp(-8 * dt));
    }
    if (p.y < DEBRIS_CONTACT_Y + 0.25) {
      flattenOntoGround(p, 1 - Math.exp(-10 * dt));
    }
  }

  if (p.y <= DEBRIS_GROUND_Y) {
    p.y = DEBRIS_GROUND_Y;
    p.onGround = true;
    if (p.vy < 0) {
      p.vy = -p.vy * p.restitution;
      p.vx *= p.friction;
      p.vz *= p.friction;
      p.wx *= DEBRIS_CONTACT_SPIN;
      p.wy *= DEBRIS_CONTACT_SPIN;
      p.wz *= DEBRIS_CONTACT_SPIN;
    }
  } else if (p.y > DEBRIS_GROUND_Y + 0.04) {
    p.onGround = false;
  }

  if (p.alignVelocity && (p.onGround || p.y < DEBRIS_CONTACT_Y)) {
    flattenOntoGround(p, 1 - Math.exp(-18 * dt));
  }

  const speed = Math.hypot(p.vx, p.vy, p.vz);
  if (speed < DEBRIS_STILL_SPEED) p.stillTime += dt;
  else p.stillTime = 0;

  const grounded = p.y < DEBRIS_CONTACT_Y;
  const supportedStill = p.stillTime > DEBRIS_STILL_DURATION;
  // Freeze only with ground/support contact, or after a long motionless dwell
  // (resting on a pile). Mid-air fragments keep falling.
  if ((grounded || supportedStill) && speed < DEBRIS_STILL_SPEED) {
    p.settled = true;
    p.vx = 0;
    p.vy = 0;
    p.vz = 0;
    p.wx = 0;
    p.wy = 0;
    p.wz = 0;
    if (p.alignVelocity) flattenOntoGround(p, 1);
  } else if (p.onGround && speed < DEBRIS_SETTLE_SPEED && grounded) {
    p.settled = true;
    p.vx = 0;
    p.vy = 0;
    p.vz = 0;
    p.wx = 0;
    p.wy = 0;
    p.wz = 0;
    if (p.alignVelocity) flattenOntoGround(p, 1);
  }
}

export function debrisLifeScale(age: number, life: number): number {
  const t = Math.min(1, Math.max(0, age / Math.max(life, 1e-6)));
  if (t <= DEBRIS_FADE_START) return 1;
  return Math.max(0, 1 - (t - DEBRIS_FADE_START) / (1 - DEBRIS_FADE_START));
}

export function randomUnitQuat(u0: number, u1: number, u2: number): QuatState {
  const u = Math.min(1, Math.max(0, u0));
  const v = Math.min(1, Math.max(0, u1));
  const w = Math.min(1, Math.max(0, u2));
  const s1 = Math.sqrt(1 - u);
  const s2 = Math.sqrt(u);
  return {
    qx: s1 * Math.sin(2 * Math.PI * v),
    qy: s1 * Math.cos(2 * Math.PI * v),
    qz: s2 * Math.sin(2 * Math.PI * w),
    qw: s2 * Math.cos(2 * Math.PI * w),
  };
}

export function debrisPoolLayout(totalSlots: number): { perFamily: number; perVariant: number } {
  const perFamily = Math.max(1, Math.floor(Math.max(totalSlots, 0) / DEBRIS_FAMILIES.length));
  const perVariant = Math.max(1, Math.floor(perFamily / DEBRIS_SHARD_VARIANTS));
  return { perFamily, perVariant };
}

/** Shrink a card that is close to the camera or would otherwise dominate the frame. */
export function dustApparentScale(
  scale: number,
  viewDistance: number,
  y = 1,
  apron = false,
): number {
  const near = dustNearFade(viewDistance);
  const factor = apron
    ? DUST_APRON_SCREEN_RADIUS
    : y < DUST_GROUND_HUG_Y
      ? DUST_GROUND_SCREEN_RADIUS
      : DUST_SCREEN_RADIUS;
  const cap = Math.max(0.05, viewDistance * factor);
  return Math.min(Math.max(scale, 0), cap) * near;
}

/** How many overlapping cards a puff of this scale should occupy. */
export function dustClusterCopies(scale: number, u: number): number {
  if (scale <= DUST_CLUSTER_SCALE_THRESHOLD) return 1;
  return u < 0.45 ? 2 : 3;
}

/** Per-copy scale inside a cluster (60–75% of the logical puff). */
export function dustClusterScale(scale: number, u: number): number {
  return scale * (DUST_CLUSTER_SCALE_MIN + (DUST_CLUSTER_SCALE_MAX - DUST_CLUSTER_SCALE_MIN) * Math.min(1, Math.max(0, u)));
}

/** Isolated large cards drop opacity so they read as haze, not plates. */
export function dustScaleLinkedAlpha(alpha: number, scale: number): number {
  const cap = Math.min(1, Math.max(DUST_SCALE_ALPHA_MIN, DUST_SCALE_ALPHA_REF / Math.max(scale, 1e-6)));
  return alpha * cap;
}

/** 0 at the floor, 1 at `DUST_FLOOR_FADE_Y`. Apron callers skip this. */
export function dustFloorFade(y: number): number {
  const t = Math.min(1, Math.max(0, y / DUST_FLOOR_FADE_Y));
  return t * t * (3 - 2 * t);
}

export function dustFloorAlphaMul(y: number): number {
  const t = dustFloorFade(y);
  return DUST_FLOOR_ALPHA_MIN + (1 - DUST_FLOOR_ALPHA_MIN) * t;
}

export function dustFloorScaleMul(y: number): number {
  const t = dustFloorFade(y);
  return DUST_FLOOR_SCALE_MIN + (1 - DUST_FLOOR_SCALE_MIN) * t;
}

/** Cool grey-beige: kill the red cast that reads as brick clay through alpha. */
export function coolMortarRgb(rgb: { r: number; g: number; b: number }): {
  r: number;
  g: number;
  b: number;
} {
  const avg = (rgb.r + rgb.g + rgb.b) / 3;
  return fitLuminance(
    {
      r: rgb.r * 0.22 + avg * 0.62 + 0.06,
      g: rgb.g * 0.22 + avg * 0.64 + 0.08,
      b: rgb.b * 0.18 + avg * 0.66 + 0.1,
    },
    DUST_MORTAR_LUMINANCE,
  );
}

/** Push clay toward a saturated terracotta so it cannot share mortar's pale. */
export function saturateClayRgb(rgb: { r: number; g: number; b: number }): {
  r: number;
  g: number;
  b: number;
} {
  return fitLuminance(
    {
      r: Math.min(1, rgb.r * 1.2 + 0.08),
      g: rgb.g * 0.62,
      b: rgb.b * 0.48,
    },
    DUST_CLAY_LUMINANCE,
  );
}

export function isRubbleApronShape(shape: WeaponBurstShape): boolean {
  return shape.along < 0.2 && shape.burstRadial > 1.2;
}

/** Particles per cubic metre of pulverized volume, before the fallback cap. */
export const PULVERIZE_PARTICLES_PER_M3 = 4200;
/** Mobile-cap pulverize count. High-tier 280 died with the GPU/CPU split. */
export const PULVERIZE_COUNT_CAP = 140;
/** Volume (m³) at which size/speed/linger multipliers saturate. */
export const PULVERIZE_VOLUME_REF = 0.08;

export interface PulverizedDustBudget {
  count: number;
  sizeMul: number;
  speedMul: number;
  lingerMul: number;
}

/**
 * Map crushed mass → CPU fallback cloud budget. A light tap is a puff; a
 * brick bomb saturates the mobile cap. `off` and empty volume emit nothing.
 */
export function pulverizedDustBudget(
  volume: number,
  tier: DemolitionEffectTier,
): PulverizedDustBudget {
  const v = Number.isFinite(volume) && volume > 0 ? volume : 0;
  const cap = tier === 'off' ? 0 : PULVERIZE_COUNT_CAP;
  if (cap <= 0 || v <= 1e-8) {
    return { count: 0, sizeMul: 1, speedMul: 1, lingerMul: 1 };
  }
  const raw = Math.round(v * PULVERIZE_PARTICLES_PER_M3);
  const count = Math.min(cap, Math.max(v > 0.0004 ? 24 : 6, raw));
  const t = Math.min(1, v / PULVERIZE_VOLUME_REF);
  return {
    count,
    sizeMul: 0.95 + t * 1.15,
    speedMul: 0.5 + t * 1.15,
    lingerMul: 0.7 + t * 0.9,
  };
}

export function twoToneBurstBias(mortar: boolean): {
  along: number;
  spreadRad: number;
  speedMul: number;
  scaleMul: number;
} {
  return mortar
    ? {
        along: DUST_MORTAR_ALONG,
        spreadRad: DUST_MORTAR_SPREAD_RAD,
        speedMul: DUST_MORTAR_SPEED_MUL,
        scaleMul: DUST_MORTAR_SCALE_MUL,
      }
    : {
        along: DUST_CLAY_ALONG,
        spreadRad: DUST_CLAY_SPREAD_RAD,
        speedMul: DUST_CLAY_SPEED_MUL,
        scaleMul: DUST_CLAY_SCALE_MUL,
      };
}
